import {useMemo} from 'react'
import type {Moment} from 'moment-timezone'
import moment from 'moment-timezone'
import {Products} from '@freckle/educator-entities/ts/common/helpers/products'
import {useAsyncMap} from '@freckle/educator-entities/ts/common/hooks/use-async/index'
import {toHTMLTextDangerous} from '@freckle/educator-entities/ts/common/models/html-text'
import type {MathQuestionAttrs} from '@freckle/educator-entities/ts/math/common/models/questions'
import {fetchMathQuestionById} from '@freckle/educator-entities/ts/math/common/models/questions'
import {
  WithResources,
  WithResourcesArray,
} from '@freckle/classroom/ts/common/components/with-resources'
import {useQueryParamAsString} from '@freckle/classroom/ts/hooks/use-params'
import {useTeacher} from '@freckle/classroom/ts/hooks/use-teacher'
import type {TeacherAttrs} from '@freckle/classroom/ts/users/models/teacher'
import {Printables} from '../printables'
import type {Printable} from '../types'
import {partitionQuestions} from '../types'

export default function PrintablesExampleWrapper() {
  const extraQuestionId = useQueryParamAsString('includeQuestion')
  const teacher = useTeacher()

  return (
    <WithResources
      resources={{teacher}}
      render={data => <Inner extraQuestionId={extraQuestionId} {...data} />}
    />
  )
}

const includeExtra = (questionIds: string[], extra: string | null): string[] =>
  extra === null ? questionIds : [extra].concat(questionIds)

const Inner = ({
  teacher,
  extraQuestionId,
}: {
  teacher: TeacherAttrs
  extraQuestionId: string | null
}) => {
  const questions = useAsyncMap(
    includeExtra(examplePrintableIds, extraQuestionId),
    questionId => fetchMathQuestionById(questionId, teacher.language),
    'fetchMathQuestionById',
    [teacher.language],
  )
  const now = useMemo(() => moment(), [])

  return (
    <WithResourcesArray
      resources={{questions}}
      render={({questions}) => (
        <Printables
          product={Products.MathAdaptive}
          printables={mkPrintables(questions, teacher, now)}
        />
      )}
    />
  )
}

const mkPrintables = (
  questions: MathQuestionAttrs[],
  teacher: TeacherAttrs,
  createdAt: Moment,
): Printable[] => [
  {
    student: {firstName: 'Example', lastName: ''},
    standardConcept: 'Example Printable',
    standard: null,
    description: toHTMLTextDangerous('Example'),
    content: partitionQuestions(questions),
    language: teacher.language,
    createdAt,
  },
]

const examplePrintableIds = [
  '773331dada3c', // graph_linear_inequality
  'a7684f2fcf51', // multiple_choice
  '2e85f7e76757', // graph_scatter_points
  '765434ab0ef0', // singular_or
  '28d5ab2c233f', // multiple_answers
  '927d44ac4366', // graph_linear
  '15b5d0b6360b', // graph_quadratic
  'c563b368df1b', // graph_exponential
  '0add833ff800', // fill_in_the_blanks
]
